import CantileverCard, {
  CantileverCardSection,
} from 'components/CantileverCard'
import { CantileverRow } from 'components/CantileverRow'
import { format } from 'date-fns'
import { StyledUploadButton } from 'features/assetDatabase/components/assetOverview/assetDetails/imagesSection/UploadImages'
import React, { useMemo } from 'react'
import styled from 'styled-components'
import {
  GeckoComponent,
  GeckoInspection,
  GeckoReport,
  GeckoSource,
} from 'types'

import { useImagesContext } from './ImagesContext'
import { ImagesRow } from './ImagesRow'

const SubSectionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
`

const NoImagesContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 2.875rem;
  align-self: stretch;
  color: ${({ theme }) => theme.forecast.tertiaryText};
`

const SubSectionNameText = styled.p`
  font-size: 0.875rem;
  font-weight: 500;
  color: ${({ theme }) => theme.forecast.activeText};
`

type ImagesSubSectionProps = {
  subSectionItem: GeckoComponent | GeckoInspection
  subSectionURLs: readonly string[]
  subSectionThumbnails: readonly GeckoReport[]
}

/**
 * This is the specific component or inspection group of images.
 */
export const ImagesSubSection: React.FC<ImagesSubSectionProps> = ({
  subSectionItem,
  subSectionURLs,
  subSectionThumbnails,
}) => {
  const { canUploadFiles } = useImagesContext()

  const isComponent = subSectionItem instanceof GeckoComponent
  const isInspection = subSectionItem instanceof GeckoInspection

  const drillDownName = useMemo(() => {
    if (isComponent) {
      return subSectionItem.data.name
    }
    if (isInspection) {
      const date = format(subSectionItem.data.date, 'yyyy-MM-dd')
      const type = GeckoSource.GetByID(
        subSectionItem.data.inspection_type
      )?.name
      return !type ? date : `${date} ${type}`
    }
    return ''
  }, [subSectionItem, isComponent, isInspection])

  const componentID = isComponent ? subSectionItem.id : undefined
  const inspectionID = isInspection ? subSectionItem.id : undefined

  return (
    <SubSectionContainer key={subSectionItem.id}>
      <CantileverCardSection style={{ paddingLeft: 0, paddingRight: 0 }}>
        <CantileverRow
          leadingContent={
            <SubSectionNameText>{drillDownName}</SubSectionNameText>
          }
          trailingContent={
            canUploadFiles && (
              <StyledUploadButton
                isAssetThumbnail={true}
                text={''}
                componentID={componentID}
                inspectionID={inspectionID}
              />
            )
          }
        />
      </CantileverCardSection>
      <CantileverCard.Group>
        {subSectionURLs.length > 0 ? (
          <ImagesRow
            primaryImageIndex={null}
            subSectionURLs={subSectionURLs}
            subSectionThumbnails={subSectionThumbnails}
            componentID={componentID}
            inspectionID={inspectionID}
          />
        ) : (
          <NoImagesContainer>No Images</NoImagesContainer>
        )}
      </CantileverCard.Group>
    </SubSectionContainer>
  )
}
